import type {
  AxiosAdapter,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import {
  AxiosError,
  isAxiosError,
} from 'axios';

import { useWebViewStore } from '#ui-shared/stores/webview/webview-store';
import type { NoraWebViewResponse } from '#ui-shared/stores/webview/webview.types';
import type { JSONValue } from '#ui-shared/types/json';
import { Base64Utils } from '#ui-shared/utils/base64';
import { WebViewLoggingContext } from '#ui-shared/utils/logger/contexts/log-contexts';
import { appLogger } from '#ui-shared/utils/logger/LoggerClass';

export class WebViewError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WebViewError';
  }
}

export class WebViewMethodError extends WebViewError {
  constructor({ method }: { method: string }) {
    super(`Unsupported request method in WebView interceptor: ${method}`);
    this.name = 'WebViewMethodError';
  }
}

export class WebViewUrlError extends WebViewError {
  constructor() {
    super(`Request URL is not defined in WebView interceptor`);
    this.name = 'WebViewUrlError';
  }
}

export class WebViewParsingError extends WebViewError {
  constructor(message: string) {
    super(`Error parsing response in WebView interceptor: ${message}`);
    this.name = 'WebViewParsingError';
  }
}

/**
 * Represents the necessary metadata for a parsing operation.
 */
interface ParseDataMetadata {
  contentType: string;
  config: {
    url: string | undefined;
  };
}

/**
 * Convenience class to organize logic related to WebView interceptors.
 *
 * See webview.md for more information.
 */
export class WebViewInterceptors {
  // Wrapping the adapters so that we can pass in a correct axios instance, and avoid a situation in which competing axios instances generate deviating URLS

  /**
   * Provides an adapter for intercepting HTTP GET calls, which retrieves the response from the WebView store based on the request URL and payload, and returns a mocked AxiosResponse.
   *
   * The adapter is created as a higher-order function that takes an Axios instance as an argument, allowing it to generate consistent URLs for retrieving responses from the WebView store.
   * @param instance
   */
  static createGetAdapter(instance: AxiosInstance): AxiosAdapter {
    return (config: InternalAxiosRequestConfig): Promise<AxiosResponse> => WebViewInterceptors.getAdapter(instance, config);
  }

  /**
   * Provides an adapter for intercepting HTTP POST/PUT/DELETE calls.
   */
  static createMutationAdapter(): AxiosAdapter {
    return (config: InternalAxiosRequestConfig): Promise<AxiosResponse> => WebViewInterceptors.mutationAdapter(config);
  }

  /**
   * An adapter for intercepting HTTP GET calls
   * @param instance
   * @param config
   */
  private static async getAdapter(instance: AxiosInstance, config: InternalAxiosRequestConfig): Promise<AxiosResponse> {
    const webViewStore = useWebViewStore();
    if (!config.url) {
      appLogger.error({
        message: `Failure to process GET request in Webview interceptor - URL is missing`,
        context: {
          webview: new WebViewLoggingContext({
            url: config.url || 'undefined',
            method: 'GET',
          }),
        },
      });
      throw new WebViewUrlError();
    }

    const fullUrl = instance.getUri(config);
    const url = new URL(fullUrl, window.location.origin);

    const response = await webViewStore.fetchWebviewResponse(
      config.method ?? 'GET',
      url,
      config.data,
    );

    if (!response) {
      return Promise.reject(WebViewInterceptors.buildUnavailableError(config));
    }

    let mockResponse: AxiosResponse;

    try {
      // Underlying functions log errors, so we don't have to.
      mockResponse = WebViewInterceptors.parseResponseOrThrow(response, config);
    }
    catch {
      return Promise.reject(new WebViewParsingError('Failed to parse response from WebView'));
    };

    return WebViewInterceptors.settleResponse(mockResponse);
  }

  /**
   * An adapter for intercepting HTTP calls that mutate state.
   * @param config
   */
  private static async mutationAdapter(config: InternalAxiosRequestConfig): Promise<AxiosResponse> {
    const webViewStore = useWebViewStore();
    if (!config.url) {
      appLogger.error({
        message: `URL is missing in mutation request in Webview interceptor`,
        context: {
          webview: new WebViewLoggingContext({
            url: config.url || 'undefined',
            method: config.method || 'undefined',
          }),
        },
        sensitiveContext: {
          webview: new WebViewLoggingContext({
            config: JSON.stringify(config),
          }),
        },
      });
      throw new WebViewUrlError();
    }

    if (!config.method) {
      appLogger.error({
        message: `Request method is not defined in a request in Webview mutation interceptor`,
        context: {
          webview: new WebViewLoggingContext({
            url: config.url,
            method: config.method || 'undefined',
          }),
        },
        sensitiveContext: {
          webview: new WebViewLoggingContext({
            config: JSON.stringify(config),
          }),
        },
      });
      throw new WebViewMethodError({ method: config.method || 'undefined' });
    }

    const url = new URL(config.url, window.location.origin);
    webViewStore.sendRequestToNativeLayer(
      config.method,
      `${url.pathname}${url.search}`,
      config.data,
    );

    const mockResponse: AxiosResponse = {
      data: {
        message: 'offline-request',
        data: config.data ? JSON.parse(config.data) : undefined,
      },
      status: 200,
      statusText: 'OK',
      headers: { 'content-type': 'application/json' },
      config,
    };

    return Promise.resolve(mockResponse);
  }

  /**
   * Resolves the response when its status is valid, or rejects with an {@link AxiosError} otherwise.
   *
   * Mirrors Axios's internal `settle()`: a custom adapter that simply resolves a response would
   * bypass status validation entirely, so a non-2xx status from the native layer would otherwise
   * look like a success. Honoring it here lets the native layer fail a request fast (e.g. a 503
   * when it knows the device is offline) and have callers handle it like any other HTTP error.
   * @param response - the mocked response built from the native layer's reply
   * @private
   */
  private static settleResponse(response: AxiosResponse): Promise<AxiosResponse> {
    const validateStatus = response.config.validateStatus;

    if (!response.status || !validateStatus || validateStatus(response.status)) {
      return Promise.resolve(response);
    }

    return Promise.reject(WebViewInterceptors.buildStatusError(response));
  }

  /**
   * Builds an {@link AxiosError} for a non-2xx response, matching the shape Axios produces for a
   * real HTTP error (5xx -> `ERR_BAD_RESPONSE`, otherwise `ERR_BAD_REQUEST`, with the response attached).
   * @param response - the mocked response whose status failed validation
   * @private
   */
  private static buildStatusError(response: AxiosResponse): AxiosError {
    const code = response.status >= 500 ? AxiosError.ERR_BAD_RESPONSE : AxiosError.ERR_BAD_REQUEST;

    return new AxiosError(
      `Request failed with status code ${response.status}`,
      code,
      response.config,
      undefined,
      response,
    );
  }

  /**
   * Builds an {@link AxiosError} for the case where the native layer cannot answer at all (during the
   * bootstrap window, or offline with no cached entry).
   *
   * This is modeled as a network-style error (no response, `ERR_NETWORK`) rather than a server status:
   * the native layer isn't unavailable in a "try again later" sense, it deliberately won't answer, so
   * retrying is pointless. Carrying no response means axios-retry (which only retries specific 5xx
   * responses) leaves it alone, and the response error handler doesn't log it as an HTTP error - it
   * fails fast and quietly. It is still an AxiosError, so callers can branch on isAxiosError uniformly.
   * @param config - the originating request config
   * @private
   */
  private static buildUnavailableError(config: InternalAxiosRequestConfig): AxiosError {
    return new AxiosError(
      'No offline response available',
      AxiosError.ERR_NETWORK,
      config,
    );
  }

  /**
   * Parses the response from the WebView.
   *
   * The concept here is that we "impersonate" a regular AxiosResponse, ensuring that it contains parsed data in accordance with our agreed format.
   *
   * There are two supported formats for the WebView response - a v1 format, which is simply `{ data: string }` all as raw data, and a v2 format, which includes additional fields such as headers and status code. The v2 format allows us to handle different content types (e.g. images) and to have more accurate status codes, while the v1 format is simpler and can be used for basic JSON responses.
   *
   * The parsing logic handles both formats, with a fallback to the v1 format if the expected fields for the v2 format are not present.
   * @param response
   * @param config
   * @private
   * @throws WebViewParsingError
   */
  private static parseResponseOrThrow(response: NoraWebViewResponse, config: InternalAxiosRequestConfig): AxiosResponse {
    let mockResponse: AxiosResponse | undefined;
    if ('headers' in response) { // v2 response format
      const { data, headers, status, statusText } = response as NoraWebViewResponse;

      let contentType = 'application/json';

      if (headers) {
        const contentTypeHeader = Object.keys(headers).find(
          key => key.toLowerCase() === 'content-type',
        );

        if (contentTypeHeader) {
          contentType = headers[contentTypeHeader];
        }
      }

      const parsedData: JSONValue | File | string = WebViewInterceptors.parseDataOrThrow(data, { contentType, config: { url: config.url } });

      mockResponse = {
        data: parsedData,
        status: status ?? 200,
        statusText: statusText ?? 'OK',
        headers: headers ?? { 'content-type': 'application/json' },
        config,
      };
    }
    else {
      // Fallback to v1 response format where we force content type
      mockResponse = {
        data: response.data,
        status: 200,
        statusText: 'OK',
        headers: { 'content-type': 'application/json' },
        config,
      };
    }
    return mockResponse;
  }

  /**
   * Parses the raw data from a webview response, based on its content type.
   *
   * Returns `JSONValue` if the data parsed is valid JSON; `File` if it is an image; and raw `string` for any other content type. Throws if the data is expected to be JSON or an image but fails to parse correctly.
   * @param data
   * @param metadata
   * @private
   * @throws WebViewParsingError
   */
  private static parseDataOrThrow(data: string, metadata: ParseDataMetadata): JSONValue | File | string {
    if (metadata.contentType.includes('application/json')) {
      try {
        return Base64Utils.fromBase64ToJsonOrThrow(data);
      }
      catch (error) {
        appLogger.error({
          message: `Failure to parse JSON response in Webview interceptor`,
          context: {
            webview: new WebViewLoggingContext({
              url: metadata.config.url || 'undefined',
              method: 'GET',
            }),
          },
          sensitiveContext: {
            error,
          },
        });
        throw error;
      }
    }
    else if (metadata.contentType.startsWith('image/')) {
      try {
        return Base64Utils.fromBase64ToImageOrThrow(data, metadata.contentType);
      }
      catch (error) {
        appLogger.error({
          message: `Failure to parse image response in Webview interceptor.`,
          context: {
            webview: new WebViewLoggingContext({
              url: metadata.config.url || 'undefined',
              method: 'GET',
            }),
            contentType: metadata.contentType,
          },
        });
        throw error;
      }
    }
    else {
      appLogger.warn({
        message: `Unsupported content type in Webview interceptor, returning raw data`,
        context: {
          webview: new WebViewLoggingContext({
            url: metadata.config.url || 'undefined',
            method: 'GET',
          }),
          contentType: metadata.contentType,
        },
      });

      return data;
    }
  }
}

export function setupWebViewInterceptors(axios: AxiosInstance): void {
  const webviewGetAdapter = WebViewInterceptors.createGetAdapter(axios);
  const webviewMutationAdapter = WebViewInterceptors.createMutationAdapter();

  // This config allows us to override the adapter for specific endpoints
  // For example - the /geo/route endpoint is a POST request, but it does not mutate data, it just retrieves a route but has a
  // payload
  const overrideAdapter = {
    '/geo/route': webviewGetAdapter,
    '/geo/complex-search': webviewGetAdapter,
  };

  axios.interceptors.request.use(
    (config) => {
      if (!config.method) {
        appLogger.error({
          message: `Request method is not defined in Webview interceptor`,
          context: {
            webview: new WebViewLoggingContext({
              url: config.url || 'undefined',
              method: 'undefined',
            }),
          },
          sensitiveContext: {
            webview: new WebViewLoggingContext({
              config: JSON.stringify(config),
            }),
          },
        });
        throw new WebViewMethodError({ method: 'undefined' });
      }

      const url = new URL(config.url || '', window.location.origin);
      const path = url.pathname;

      for (const [key, adapter] of Object.entries(overrideAdapter)) {
        if (path.endsWith(key)) {
          config.adapter = adapter;
          return config;
        }
      }

      switch (config.method.toLowerCase()) {
        case 'get': {
          config.adapter = webviewGetAdapter;

          break;
        }

        case 'post':
        case 'put':
        case 'delete':
        case 'patch': {
          config.adapter = webviewMutationAdapter;

          break;
        }

        default: {
          appLogger.warn({
            message: `Unsupported request method in Webview interceptor. Not intercepting!`,
            context: {
              webview: new WebViewLoggingContext({
                url: config.url || 'undefined',
                method: config.method,
              }),
            },
          });
        }
      }

      return config;
    },
    (error) => {
      if (isAxiosError(error)) {
        appLogger.error({
          message: `Failed to process request in Webview interceptor - HTTP error response`,
          context: {
            webview: new WebViewLoggingContext({}),
          },
          sensitiveContext: {
            error,
          },
        });
      }
      // Implicitly NOT handling WebViewError here, as these already output appropriate logs when they are thrown.

      return Promise.reject(error);
    },
  );

  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      return Promise.reject(error);
    },
  );
}
